import { format } from "date-fns";

const ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;
const SECONDS_FRACTION = /\d{2}:\d{2}:\d{2}(\.\d+)/;
const LOCAL_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})(\.\d+)?$/;

/**
 * The V1 log handler returns OpenSearch's `timestamp` as stored: UTC, with no
 * zone and microsecond precision ("2026-10-21T18:04:05.123456"). `new Date()`
 * reads a zone-less string as *local* time, so the zone is made explicit before
 * parsing. The result is in the browser's time, the same clock the range picker
 * above the table uses (CTX-1337), as `yyyy-MM-dd HH:mm:ss.ffffff`: a `Date`
 * stops at milliseconds, so the raw fraction is carried over as written
 * (CTX-1354). The string sorts chronologically. An unparseable value is
 * returned as-is.
 */
export function formatLogTimestamp(raw: string): string {
  if (!raw) return "";
  const normalized = raw
    .trim()
    .replace(" ", "T")
    .replace(/(\.\d{3})\d+/, "$1");
  const iso = ZONE_SUFFIX.test(normalized) ? normalized : `${normalized}Z`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return raw;
  const fraction = raw.match(SECONDS_FRACTION)?.[1] ?? "";
  return `${format(date, "yyyy-MM-dd HH:mm:ss")}${fraction}`;
}

export interface LogTimestampParts {
  /** `HH:mm:ss` */
  time: string;
  /** `.ffffff`, or "" when the log carried none */
  fraction: string;
  /** Short date, e.g. "21 Oct 2026" */
  date: string;
}

/** Splits a `formatLogTimestamp` value for the two-line cell; null if it is not one. */
export function splitLogTimestamp(value: string): LogTimestampParts | null {
  const match = value.match(LOCAL_TIMESTAMP);
  if (!match) return null;
  const [, year, month, day, time, fraction = ""] = match;
  const date = format(
    new Date(Number(year), Number(month) - 1, Number(day)),
    "d MMM yyyy"
  );
  return { time, fraction, date };
}
